/**
 * Projects-V3 read-only stores: Programs, Goals, Processes,
 * ProjectActivity, ProjectComments. Each is keyed by workspace slug (or
 * project id for project-scoped collections) and follows the same shallow
 * cache pattern used elsewhere in the codebase.
 */

import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";

import { pollExportStatus } from "@/components/data-table/use-csv-export";
import {
  GoalsService,
  ProcessesService,
  ProgramActivityService,
  ProgramCommentService,
  ProgramsService,
  ProjectActivityService,
  ProjectCommentService,
  ProjectLinkedSubmissionsService,
  ProjectV3Service,
} from "@/services/projects-v3.service";
import type {
  IProcessCreatePayload,
  IProcessStagePayload,
  IProcessesListQuery,
  TProcessUpdatePayload,
} from "@/services/projects-v3.service";
import { PermissionService } from "@/services/permission.service";
import type {
  TGoal,
  TGoalCreatePayload,
  TGoalMetricCreatePayload,
  TGoalMetricDatapoint,
  TGoalMetricDatapointCreatePayload,
  TGoalMetricDatapointUpdatePayload,
  TGoalMetricUpdatePayload,
  TGoalUpdatePayload,
  TPartialProject,
  TProgramLinkedRequest,
  TProcess,
  TProcessStage,
  TProcessUsage,
  TProgram,
  TProgramHealth,
  TProgramCreatePayload,
  TProgramUpdatePayload,
  TProjectActivity,
  TProjectComment,
  TProjectSubmissionLink,
} from "@/types/project";

import type { RootStore } from "./root.store";
import { patchFeed, readFeedInto, readNextFeedPageInto, type FeedProjection } from "./cached-feed";
import { queryCacheScope } from "./query-cache-scope";
import { getQueryClient, peekQueryClient } from "@/queries/client";
import { patchScoped } from "@/queries/invalidation";
import { projectDetailKey } from "@/queries/projects";
import {
  projectActivitiesKey,
  projectActivitiesQuery,
  projectCommentsKey,
  projectCommentsQuery,
  projectLinkedSubmissionsKey,
  projectLinkedSubmissionsQuery,
} from "@/queries/project-resources";

// Shared `[]` sentinels for the "workspace has no cache entry yet" branch of
// the read-only getters below (e.g. a permission-denied fetch that never
// populates the map). A literal `[]` there would be a NEW reference on every
// call, and a caller feeding that straight into a `useMemo` dependency array
// (SurveyJS's requester-flow choice sources, NG-1879) sees a "changed" input
// on every render even though nothing loaded — recomputing, and rebuilding
// the survey Model, forever. Returning the SAME empty array for a given
// workspace-slug/absent-cache case keeps that dependency stable instead.
const EMPTY_PROGRAMS: TProgram[] = [];
const EMPTY_GOALS: TGoal[] = [];

/** The keys of a program update whose value differs from the stored program
 *  (NG-2059). A key sent as `undefined` never reaches the server, and
 *  `team_access` is read as `{id, name}` but written as ids, so compare ids. */
function programChangedFields(data: TProgramUpdatePayload, before: TProgram | undefined): string[] {
  const sent = Object.fromEntries(Object.entries(data).filter(([, value]) => value !== undefined));
  const stored = before && { ...before, team_access: before.team_access.map((team) => team.id) };
  return changedFields(sent, stored);
}

export class ProgramsStore {
  programsByWorkspace = observable.map<string, TProgram[]>();
  // Selection-mode cache (#2792): the strict team-family visibility set the
  // program pickers (Requests/Projects) render. Kept separate from
  // `programsByWorkspace` (the full list-page view) so a picker fetch never
  // clobbers the list page's broader set, and vice versa.
  selectableProgramsByWorkspace = observable.map<string, TProgram[]>();
  projectsByProgram = observable.map<string, TPartialProject[]>();
  // A program's linked requests (NG-1630), keyed by program id. Separate from
  // `projectsByProgram` because they answer different questions: a linked
  // PROJECT is delivery under this program, a linked REQUEST is an ask tied to
  // it (at intake, or from the program page since NG-1734), and a request never
  // becomes a project by being linked.
  requestsByProgram = observable.map<string, TProgramLinkedRequest[]>();
  // Per-workspace "can the caller create a program at all?" verdict, backing the
  // "New Program" button gate (#3206). `undefined` = not yet answered (the
  // button stays hidden to avoid a flash); a boolean is the cached probe result.
  // Program create is a team-lead capability post-flip (#3014), so this is a
  // single untargeted `program.create` can-check, not a workspace-permission
  // read — a plain member holds no workspace-scope program.create.
  canCreateByWorkspace = observable.map<string, boolean>();
  loading = false;
  // Last programs-load failure, surfaced to the DataTable `error` prop. Cleared
  // when a (re)fetch starts; set when fetch rejects.
  loadError: Error | null = null;
  private service: ProgramsService;
  private permissionService: PermissionService;

  constructor(
    _rootStore: RootStore,
    service?: ProgramsService,
    permissionService?: PermissionService,
  ) {
    this.service = service ?? new ProgramsService();
    this.permissionService = permissionService ?? new PermissionService();
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  getAll(workspaceSlug: string): TProgram[] {
    const stored = this.programsByWorkspace.get(workspaceSlug);
    return Array.isArray(stored) ? stored : EMPTY_PROGRAMS;
  }

  /** @deprecated use getAll */
  get(workspaceSlug: string): TProgram[] {
    return this.getAll(workspaceSlug);
  }

  getById(workspaceSlug: string, programId: string): TProgram | undefined {
    return this.getAll(workspaceSlug).find((p) => p.id === programId);
  }

  /** Team-family (selection-mode) programs for the pickers — a strict subset of
   *  what the list page shows. Populated by {@link fetchSelectable}. */
  getSelectable(workspaceSlug: string): TProgram[] {
    const stored = this.selectableProgramsByWorkspace.get(workspaceSlug);
    return Array.isArray(stored) ? stored : [];
  }

  getProjects(programId: string): TPartialProject[] {
    const stored = this.projectsByProgram.get(programId);
    return Array.isArray(stored) ? stored : [];
  }

  getRequests(programId: string): TProgramLinkedRequest[] {
    const stored = this.requestsByProgram.get(programId);
    return Array.isArray(stored) ? stored : [];
  }

  /** Cached "can the caller create a program?" verdict for a workspace, or
   *  `undefined` until {@link fetchCanCreate} has answered. Callers gate the
   *  "New Program" button on `=== true` so it stays hidden while unanswered
   *  (avoids a flash) and when the caller cannot create. */
  getCanCreate(workspaceSlug: string): boolean | undefined {
    return this.canCreateByWorkspace.get(workspaceSlug);
  }

  /** Probe whether the caller can create a program in this workspace and cache
   *  the verdict. This is the untargeted `program.create` capability check the
   *  can-endpoint answers as "could I create under some team I lead?" (#3206):
   *  the button renders before any team is picked, so there is no `team_id`. */
  async fetchCanCreate(workspaceSlug: string): Promise<boolean> {
    const [result] = await this.permissionService.can(workspaceSlug, [
      { action: "program.create" },
    ]);
    const can = result?.can ?? false;
    runInAction(() => {
      this.canCreateByWorkspace.set(workspaceSlug, can);
    });
    return can;
  }

  async fetch(workspaceSlug: string, includeArchived = false): Promise<TProgram[]> {
    this.loading = true;
    this.loadError = null;
    try {
      const rows = await this.service.list(workspaceSlug, includeArchived);
      runInAction(() => {
        this.programsByWorkspace.set(workspaceSlug, rows);
      });
      return rows;
    } catch (err) {
      runInAction(() => {
        this.loadError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Fetch the team-family (selection-mode) programs for the Requests/Projects
   *  pickers into the {@link selectableProgramsByWorkspace} cache. */
  async fetchSelectable(workspaceSlug: string, includeArchived = false): Promise<TProgram[]> {
    const rows = await this.service.list(workspaceSlug, includeArchived, {
      visibility: "selection",
    });
    runInAction(() => {
      this.selectableProgramsByWorkspace.set(workspaceSlug, rows);
    });
    return rows;
  }

  /** Fetch a single program's detail (which carries `can_edit` + fresh
   *  `team_access`) and upsert it into the workspace cache. The list read sets
   *  `can_edit` to `null` on every row by design, so any surface that gates on
   *  it (e.g. the overview panel) must hydrate through here. */
  async retrieve(workspaceSlug: string, programId: string): Promise<TProgram> {
    const program = await this.service.retrieve(workspaceSlug, programId);
    runInAction(() => {
      const existing = this.programsByWorkspace.get(workspaceSlug) ?? [];
      const found = existing.some((p) => p.id === programId);
      this.programsByWorkspace.set(
        workspaceSlug,
        found ? existing.map((p) => (p.id === programId ? program : p)) : [...existing, program],
      );
    });
    return program;
  }

  async create(
    workspaceSlug: string,
    data: TProgramCreatePayload,
    source: AnalyticsSource = "page",
  ): Promise<TProgram> {
    const program = await this.service.create(workspaceSlug, data);
    runInAction(() => {
      const existing = this.programsByWorkspace.get(workspaceSlug) ?? [];
      this.programsByWorkspace.set(workspaceSlug, [...existing, program]);
    });
    track("program_created", { program_id: program.id, source });
    return program;
  }

  async update(
    workspaceSlug: string,
    programId: string,
    data: TProgramUpdatePayload,
    source: AnalyticsSource = "page",
  ): Promise<TProgram> {
    // Read before the write: `fields` is what this save changed (NG-2059).
    const fields = programChangedFields(data, this.getById(workspaceSlug, programId));
    const updated = await this.service.update(workspaceSlug, programId, data);
    runInAction(() => {
      const existing = this.programsByWorkspace.get(workspaceSlug) ?? [];
      this.programsByWorkspace.set(
        workspaceSlug,
        existing.map((p) => (p.id === programId ? updated : p)),
      );
    });
    track("program_updated", { program_id: programId, fields, source });
    return updated;
  }

  async destroy(
    workspaceSlug: string,
    programId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.service.destroy(workspaceSlug, programId);
    track("program_deleted", { program_id: programId, source });
    runInAction(() => {
      const existing = this.programsByWorkspace.get(workspaceSlug) ?? [];
      this.programsByWorkspace.set(
        workspaceSlug,
        existing.filter((p) => p.id !== programId),
      );
      this.projectsByProgram.delete(programId);
    });
  }

  async fetchRequests(workspaceSlug: string, programId: string): Promise<TProgramLinkedRequest[]> {
    const rows = await this.service.listRequests(workspaceSlug, programId);
    runInAction(() => {
      this.requestsByProgram.set(programId, rows);
    });
    return rows;
  }

  /** Link several requests at once, optimistically (NG-1734).
   *
   *  The caller passes each row already filled in — the picker builds it from
   *  the requests-list row the person chose, which carries the name, status,
   *  form and date — so the rows land in the table at once. Each write then runs
   *  on its own: a success swaps in the SERVER's row (the one source for
   *  "requested by", which a list row names only by id), and a failure takes
   *  that one row back out, leaving its siblings in place. The server gates each
   *  link on `request.edit` on that request, so a mixed batch is a real
   *  outcome, not an edge case.
   *
   *  Resolves, never rejects, with the rows that failed, so the page can say
   *  how many did not go through and why. */
  async linkRequests(
    workspaceSlug: string,
    programId: string,
    rows: TProgramLinkedRequest[],
    source: AnalyticsSource = "page",
  ): Promise<{ failed: { row: TProgramLinkedRequest; error: unknown }[] }> {
    const alreadyLinked = new Set(this.getRequests(programId).map((row) => row.id));
    const toLink = rows.filter((row) => !alreadyLinked.has(row.id));
    if (toLink.length === 0) return { failed: [] };

    runInAction(() => {
      this.requestsByProgram.set(programId, [...toLink, ...this.getRequests(programId)]);
    });

    const outcomes = await Promise.allSettled(
      toLink.map((row) => this.service.linkRequestToProgram(workspaceSlug, programId, row.id)),
    );

    const failed: { row: TProgramLinkedRequest; error: unknown }[] = [];
    runInAction(() => {
      // Read the list NOW, not before the writes: an unlink or a refetch may
      // have landed while they were in flight.
      let current = this.getRequests(programId);
      outcomes.forEach((outcome, index) => {
        const row = toLink[index];
        if (outcome.status === "fulfilled") {
          current = current.map((r) => (r.id === row.id ? outcome.value : r));
        } else {
          failed.push({ row, error: outcome.reason });
          current = current.filter((r) => r.id !== row.id);
        }
      });
      this.requestsByProgram.set(programId, current);
    });

    for (const { row, error } of failed) {
      console.error("programs: failed to link request", {
        workspaceSlug,
        programId,
        submissionId: row.id,
        error,
      });
    }
    const linked = toLink.length - failed.length;
    if (linked > 0) {
      track("program_requests_linked", { program_id: programId, count: linked, source });
    }
    return { failed };
  }

  /** Unlink optimistically: the row leaves the table at once and comes back if
   *  the write fails, per the design system's optimistic-by-default rule. */
  async unlinkRequest(
    workspaceSlug: string,
    programId: string,
    submissionId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const before = this.getRequests(programId);
    runInAction(() => {
      this.requestsByProgram.set(
        programId,
        before.filter((row) => row.id !== submissionId),
      );
    });
    try {
      await this.service.unlinkRequestFromProgram(workspaceSlug, programId, submissionId);
      track("program_request_unlinked", {
        program_id: programId,
        request_id: submissionId,
        source,
      });
    } catch (error) {
      runInAction(() => {
        this.requestsByProgram.set(programId, before);
      });
      console.error("programs: failed to unlink request", {
        workspaceSlug,
        programId,
        submissionId,
        error,
      });
      throw error;
    }
  }

  async fetchProjects(workspaceSlug: string, programId: string): Promise<TPartialProject[]> {
    const rows = await this.service.listProjects(workspaceSlug, programId);
    runInAction(() => {
      this.projectsByProgram.set(programId, rows);
    });
    return rows;
  }

  async addProjects(
    workspaceSlug: string,
    programId: string,
    projectIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<TProgram> {
    const updated = await this.service.addProjects(workspaceSlug, programId, projectIds);
    runInAction(() => {
      const existing = this.programsByWorkspace.get(workspaceSlug) ?? [];
      this.programsByWorkspace.set(
        workspaceSlug,
        existing.map((p) => (p.id === programId ? updated : p)),
      );
      this.projectsByProgram.delete(programId);
    });
    track("program_projects_added", { program_id: programId, count: projectIds.length, source });
    return updated;
  }

  async removeProject(
    workspaceSlug: string,
    programId: string,
    projectId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.service.removeProject(workspaceSlug, programId, projectId);
    track("program_project_removed", { program_id: programId, project_id: projectId, source });
    runInAction(() => {
      const existing = this.projectsByProgram.get(programId) ?? [];
      this.projectsByProgram.set(
        programId,
        existing.filter((p) => p.id !== projectId),
      );
    });
  }

  async archive(
    workspaceSlug: string,
    programId: string,
    source: AnalyticsSource = "page",
  ): Promise<TProgram> {
    const updated = await this.service.archive(workspaceSlug, programId);
    track("program_archived", { program_id: programId, source });
    runInAction(() => {
      const existing = this.programsByWorkspace.get(workspaceSlug) ?? [];
      this.programsByWorkspace.set(
        workspaceSlug,
        existing.map((p) => (p.id === programId ? updated : p)),
      );
    });
    return updated;
  }

  async unarchive(
    workspaceSlug: string,
    programId: string,
    source: AnalyticsSource = "page",
  ): Promise<TProgram> {
    const updated = await this.service.unarchive(workspaceSlug, programId);
    track("program_restored", { program_id: programId, source });
    runInAction(() => {
      const existing = this.programsByWorkspace.get(workspaceSlug) ?? [];
      this.programsByWorkspace.set(
        workspaceSlug,
        existing.map((p) => (p.id === programId ? updated : p)),
      );
    });
    return updated;
  }

  /** Fetch health rollup for a single program. Result is not cached — call on demand. */
  /** Fetch the health rollup for a single program. Not cached — call on demand.
   *  Read only by the flag-off overview (NG-337 removes it with the flag). */
  getHealth(workspaceSlug: string, programId: string): Promise<TProgramHealth> {
    return this.service.getHealth(workspaceSlug, programId);
  }

  /** Export the programs list as CSV, scoped to the caller's current filters
   *  (`options`) and (when `ids` is non-empty) restricted to the selected rows. */
  exportProgramsCsv(
    workspaceSlug: string,
    ids: string[],
    options: {
      q?: string;
      status?: string[];
      include_archived?: boolean;
      goals?: string[];
      /** Home team ids (NG-2163), sent as repeated `home_team=<id>`. */
      home_team?: string[];
    } = {},
    columns?: string[],
  ): Promise<Response> {
    const body: { ids?: string[]; columns?: string[] } = {};
    if (ids.length > 0) body.ids = ids;
    if (columns && columns.length > 0) body.columns = columns;
    return this.service.exportPrograms(workspaceSlug, body, options);
  }

  pollExport(workspaceSlug: string, token: string): Promise<string | null> {
    return pollExportStatus(token, (tok) => this.service.getExportStatus(workspaceSlug, tok));
  }
}

export class GoalsStore {
  goalsByWorkspace = observable.map<string, TGoal[]>();
  canCreateByWorkspace = observable.map<string, boolean>();
  /** A metric's full series, effective_date-ascending (NG-336). */
  datapointsByMetric = observable.map<string, TGoalMetricDatapoint[]>();
  loading = false;
  /** Last ``fetch`` failure, cleared on the next attempt. Mirrors ProgramsStore:
   *  a surface that offers a retry (the report builder's goal filter, NG-622)
   *  needs the already-recorded error rather than its own catch. */
  loadError: Error | null = null;
  private service: GoalsService;
  private permissionService: PermissionService;
  /** In-flight full-list reads, keyed by slug, so concurrent `ensure` callers
   *  share one request. Not observable — nothing renders off it. */
  private inflightList = new Map<string, Promise<TGoal[]>>();

  constructor(
    _rootStore: RootStore,
    service?: GoalsService,
    permissionService?: PermissionService,
  ) {
    this.service = service ?? new GoalsService();
    this.permissionService = permissionService ?? new PermissionService();
    makeAutoObservable(this, { rootStore: false, inflightList: false } as never, {
      autoBind: true,
    });
  }

  /**
   * Load the workspace's goals unless they are already cached, sharing one
   * request across concurrent callers.
   *
   * Keyed on whether the slug has an ENTRY, not on whether that entry has
   * length: a workspace with no goals caches `[]`, and a `length === 0` guard
   * cannot tell that apart from "never loaded", so every caller re-fetches on
   * every open forever. Two surfaces hand-rolled that guard before this existed
   * (the dashboard goals chip and its active pill).
   *
   * Swallows nothing: a failed read rejects, and this passes that rejection
   * straight through, so a caller that cannot act on it still has to say so
   * (see NG-1280 for the sibling stores whose callers currently do not).
   */
  async ensure(workspaceSlug: string): Promise<TGoal[]> {
    const cached = this.goalsByWorkspace.get(workspaceSlug);
    if (cached) return cached;
    const inflight = this.inflightList.get(workspaceSlug);
    if (inflight) return inflight;
    const p = this.fetch(workspaceSlug).finally(() => {
      this.inflightList.delete(workspaceSlug);
    });
    this.inflightList.set(workspaceSlug, p);
    return p;
  }

  get(workspaceSlug: string): TGoal[] {
    const stored = this.goalsByWorkspace.get(workspaceSlug);
    return Array.isArray(stored) ? stored : EMPTY_GOALS;
  }

  async fetch(workspaceSlug: string): Promise<TGoal[]> {
    this.loading = true;
    this.loadError = null;
    try {
      const rows = await this.service.list(workspaceSlug);
      runInAction(() => {
        this.goalsByWorkspace.set(workspaceSlug, rows);
      });
      return rows;
    } catch (err) {
      runInAction(() => {
        this.loadError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Cached "can the caller create a goal?" verdict, or `undefined` until
   *  {@link fetchCanCreate} answers. The "New Goal" button gates on `=== true`
   *  so it stays hidden while unanswered (avoids a flash) — mirrors
   *  ProgramsStore. */
  getCanCreate(workspaceSlug: string): boolean | undefined {
    return this.canCreateByWorkspace.get(workspaceSlug);
  }

  async fetchCanCreate(workspaceSlug: string): Promise<boolean> {
    const [result] = await this.permissionService.can(workspaceSlug, [{ action: "goal.create" }]);
    const can = result?.can ?? false;
    runInAction(() => {
      this.canCreateByWorkspace.set(workspaceSlug, can);
    });
    return can;
  }

  /** Create a goal (+ its metrics) and append it to the workspace cache. The
   *  server assigns ids and normalizes decimals, so this is a blocking create
   *  (result unknowable client-side), not an optimistic one. */
  async create(
    workspaceSlug: string,
    payload: TGoalCreatePayload,
    source: AnalyticsSource = "page",
  ): Promise<TGoal> {
    const created = await this.service.create(workspaceSlug, payload);
    runInAction(() => {
      this.goalsByWorkspace.set(workspaceSlug, [...this.get(workspaceSlug), created]);
    });
    track("goal_created", {
      goal_id: created.id,
      metric_count: created.metrics?.length ?? 0,
      source,
    });
    return created;
  }

  private upsert(workspaceSlug: string, goal: TGoal): void {
    runInAction(() => {
      const existing = this.get(workspaceSlug);
      const found = existing.some((g) => g.id === goal.id);
      this.goalsByWorkspace.set(
        workspaceSlug,
        found ? existing.map((g) => (g.id === goal.id ? goal : g)) : [...existing, goal],
      );
    });
  }

  /** Fetch a goal's detail (fresh metrics + the caller's `can_edit`) and upsert
   *  it into the workspace cache — the list read sets `can_edit` to null by
   *  contract, so the edit dialog must hydrate through here. */
  async retrieve(workspaceSlug: string, goalId: string): Promise<TGoal> {
    const goal = await this.service.retrieve(workspaceSlug, goalId);
    this.upsert(workspaceSlug, goal);
    return goal;
  }

  /** PATCH the goal's own fields (name/description/status — NG-311). */
  async update(
    workspaceSlug: string,
    goalId: string,
    payload: TGoalUpdatePayload,
    source: AnalyticsSource = "page",
  ): Promise<TGoal> {
    // Read before the write: `fields` is what this save changed (NG-2148).
    const fields = changedFields(payload, this.goalById(workspaceSlug, goalId));
    const updated = await this.service.update(workspaceSlug, goalId, payload);
    this.upsert(workspaceSlug, updated);
    track("goal_updated", { goal_id: goalId, fields, source });
    return updated;
  }

  private goalById(workspaceSlug: string, goalId: string): TGoal | undefined {
    return this.get(workspaceSlug).find((g) => g.id === goalId);
  }

  /** Delete a goal (NG-616) and drop it from the workspace cache. Blocking by
   *  design — destructive and irreversible, the one sanctioned non-optimistic
   *  case — and the caller navigates away on success. */
  async deleteGoal(
    workspaceSlug: string,
    goalId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.service.deleteGoal(workspaceSlug, goalId);
    track("goal_deleted", { goal_id: goalId, source });
    runInAction(() => {
      this.goalsByWorkspace.set(
        workspaceSlug,
        this.get(workspaceSlug).filter((g) => g.id !== goalId),
      );
    });
  }

  // --- Goal Leads + goal-side work links (NG-616) — thin passthroughs; the
  // Edit Goal page owns the section state (fetched per goal, not cached
  // workspace-wide). ---

  listLeads(workspaceSlug: string, goalId: string) {
    return this.service.listLeads(workspaceSlug, goalId);
  }

  async addLead(workspaceSlug: string, goalId: string, memberId: string) {
    const lead = await this.service.addLead(workspaceSlug, goalId, memberId);
    track("goal_lead_added", { goal_id: goalId, member_id: memberId, source: "page" });
    return lead;
  }

  async removeLead(workspaceSlug: string, goalId: string, bindingId: string) {
    const result = await this.service.removeLead(workspaceSlug, goalId, bindingId);
    track("goal_lead_removed", { goal_id: goalId, source: "page" });
    return result;
  }

  listLinkedProjects(workspaceSlug: string, goalId: string) {
    return this.service.listLinkedProjects(workspaceSlug, goalId);
  }

  async linkProject(workspaceSlug: string, goalId: string, projectId: string) {
    const result = await this.service.linkProject(workspaceSlug, goalId, projectId);
    track("goal_project_linked", { goal_id: goalId, project_id: projectId, source: "page" });
    return result;
  }

  async unlinkProject(workspaceSlug: string, goalId: string, projectId: string) {
    const result = await this.service.unlinkProject(workspaceSlug, goalId, projectId);
    track("goal_project_unlinked", { goal_id: goalId, project_id: projectId, source: "page" });
    return result;
  }

  listLinkedPrograms(workspaceSlug: string, goalId: string) {
    return this.service.listLinkedPrograms(workspaceSlug, goalId);
  }

  listLinkedRequests(workspaceSlug: string, goalId: string) {
    return this.service.listLinkedRequests(workspaceSlug, goalId);
  }

  async linkRequest(workspaceSlug: string, goalId: string, submissionId: string) {
    const result = await this.service.linkRequest(workspaceSlug, goalId, submissionId);
    track("goal_request_linked", { goal_id: goalId, request_id: submissionId, source: "page" });
    return result;
  }

  async unlinkRequest(workspaceSlug: string, goalId: string, submissionId: string) {
    const result = await this.service.unlinkRequest(workspaceSlug, goalId, submissionId);
    track("goal_request_unlinked", { goal_id: goalId, request_id: submissionId, source: "page" });
    return result;
  }

  async linkProgram(workspaceSlug: string, goalId: string, programId: string) {
    const result = await this.service.linkProgram(workspaceSlug, goalId, programId);
    track("goal_program_linked", { goal_id: goalId, program_id: programId, source: "page" });
    return result;
  }

  async unlinkProgram(workspaceSlug: string, goalId: string, programId: string) {
    const result = await this.service.unlinkProgram(workspaceSlug, goalId, programId);
    track("goal_program_unlinked", { goal_id: goalId, program_id: programId, source: "page" });
    return result;
  }

  /** Metric CRUD (NG-253) — thin passthroughs; callers re-`retrieve` the goal
   *  after a batch of metric edits so the cache reflects server truth once. */
  async createMetric(workspaceSlug: string, goalId: string, payload: TGoalMetricCreatePayload) {
    const metric = await this.service.createMetric(workspaceSlug, goalId, payload);
    track("goal_metric_created", { goal_id: goalId, metric_id: metric.id, source: "page" });
    return metric;
  }

  async updateMetric(
    workspaceSlug: string,
    goalId: string,
    metricId: string,
    payload: TGoalMetricUpdatePayload,
  ) {
    // Read before the write: `fields` is what this save changed (NG-2148).
    const before = this.goalById(workspaceSlug, goalId)?.metrics.find((m) => m.id === metricId);
    const fields = changedFields(payload, before);
    const metric = await this.service.updateMetric(workspaceSlug, goalId, metricId, payload);
    track("goal_metric_updated", { goal_id: goalId, metric_id: metricId, fields, source: "page" });
    return metric;
  }

  async deleteMetric(workspaceSlug: string, goalId: string, metricId: string) {
    const result = await this.service.deleteMetric(workspaceSlug, goalId, metricId);
    track("goal_metric_deleted", { goal_id: goalId, metric_id: metricId, source: "page" });
    return result;
  }

  // --- Datapoints (NG-336). Per-row CRUD; the cache is the metric's full
  // series (the chart plots all of it), kept effective_date-ascending. After
  // every write the cached goal's metric summary (latest_datapoint /
  // datapoint_count) is recomputed locally so the Progress column refreshes
  // without a refetch. ---

  getDatapoints(metricId: string): TGoalMetricDatapoint[] {
    const stored = this.datapointsByMetric.get(metricId);
    return Array.isArray(stored) ? stored : [];
  }

  async fetchDatapoints(
    workspaceSlug: string,
    goalId: string,
    metricId: string,
  ): Promise<TGoalMetricDatapoint[]> {
    const rows = await this.service.listDatapoints(workspaceSlug, goalId, metricId);
    runInAction(() => {
      this.datapointsByMetric.set(metricId, rows);
    });
    return rows;
  }

  async createDatapoint(
    workspaceSlug: string,
    goalId: string,
    metricId: string,
    payload: TGoalMetricDatapointCreatePayload,
  ): Promise<TGoalMetricDatapoint> {
    const created = await this.service.createDatapoint(workspaceSlug, goalId, metricId, payload);
    this._setDatapoints(workspaceSlug, goalId, metricId, [
      ...this.getDatapoints(metricId),
      created,
    ]);
    track("goal_datapoint_created", {
      goal_id: goalId,
      metric_id: metricId,
      datapoint_id: created.id,
      source: "page",
    });
    return created;
  }

  async updateDatapoint(
    workspaceSlug: string,
    goalId: string,
    metricId: string,
    datapointId: string,
    payload: TGoalMetricDatapointUpdatePayload,
  ): Promise<TGoalMetricDatapoint> {
    // Read before the write: `fields` is what this save changed (NG-2148).
    const fields = changedFields(
      payload,
      this.getDatapoints(metricId).find((d) => d.id === datapointId),
    );
    const updated = await this.service.updateDatapoint(
      workspaceSlug,
      goalId,
      metricId,
      datapointId,
      payload,
    );
    this._setDatapoints(
      workspaceSlug,
      goalId,
      metricId,
      this.getDatapoints(metricId).map((d) => (d.id === datapointId ? updated : d)),
    );
    track("goal_datapoint_updated", {
      goal_id: goalId,
      metric_id: metricId,
      datapoint_id: datapointId,
      fields,
      source: "page",
    });
    return updated;
  }

  async deleteDatapoint(
    workspaceSlug: string,
    goalId: string,
    metricId: string,
    datapointId: string,
  ): Promise<void> {
    await this.service.deleteDatapoint(workspaceSlug, goalId, metricId, datapointId);
    track("goal_datapoint_deleted", {
      goal_id: goalId,
      metric_id: metricId,
      datapoint_id: datapointId,
      source: "page",
    });
    this._setDatapoints(
      workspaceSlug,
      goalId,
      metricId,
      this.getDatapoints(metricId).filter((d) => d.id !== datapointId),
    );
  }

  private _setDatapoints(
    workspaceSlug: string,
    goalId: string,
    metricId: string,
    rows: TGoalMetricDatapoint[],
  ): void {
    const sorted = [...rows].sort((a, b) => a.effective_date.localeCompare(b.effective_date));
    const last = sorted.at(-1);
    runInAction(() => {
      this.datapointsByMetric.set(metricId, sorted);
      // Mirror the server's metric summary locally (same shape the serializer
      // annotates) so goals list rows stay truthful after a write.
      this.goalsByWorkspace.set(
        workspaceSlug,
        this.get(workspaceSlug).map((g) =>
          g.id !== goalId
            ? g
            : {
                ...g,
                metrics: g.metrics.map((m) =>
                  m.id !== metricId
                    ? m
                    : {
                        ...m,
                        latest_datapoint: last
                          ? { value: last.value, effective_date: last.effective_date }
                          : null,
                        datapoint_count: sorted.length,
                      },
                ),
              },
        ),
      );
    });
  }
}

export class ProcessesStore {
  pickableByWorkspace = observable.map<string, { id: string; name: string }[]>(undefined, {
    deep: false,
  });
  pickableLoading = observable.set<string>();
  private inflightPickable = new Map<string, Promise<{ id: string; name: string }[]>>();
  processesByWorkspace = observable.map<string, TProcess[]>();
  loading = false;
  // Last full-bank load failure. Cleared when a (re)fetch starts; set when
  // `fetch` rejects. NOT what the settings table renders — `process-list.tsx`
  // binds `pageError` (the paginated cache below) to the DataTable `error` prop,
  // so this only surfaces wherever a caller reads it directly. A post-write
  // refresh failure lands here too, and is deliberately not re-thrown: see
  // `syncAfterWrite`.
  loadError: Error | null = null;

  // --- Paginated settings-list cache (parallel to processesByWorkspace) ------
  // The settings Processes table is server-driven (filters/sort/pagination); the
  // full bank above stays what the template-form process pickers + work-item
  // panels read via `get`/`getById`.
  pagedProcesses: TProcess[] = [];
  pageInfo: { totalCount: number; hasNext: boolean; nextCursor: string | null } = {
    totalCount: 0,
    hasNext: false,
    nextCursor: null,
  };
  isPageLoading = false;
  isLoadingMore = false;
  pageError: Error | null = null;
  private lastPageQuery: IProcessesListQuery = {};
  private service: ProcessesService;
  /** In-flight {@link ensure} loads, keyed by slug. Deliberately not
   *  observable: request bookkeeping, not state anyone renders. */
  private inflightBank = new Map<string, Promise<TProcess[]>>();

  constructor(_rootStore: RootStore, service?: ProcessesService) {
    this.service = service ?? new ProcessesService();
    makeAutoObservable(
      this,
      {
        rootStore: false,
        lastPageQuery: false,
        inflightBank: false,
        inflightPickable: false,
      } as never,
      {
        autoBind: true,
      },
    );
  }

  get(workspaceSlug: string): TProcess[] {
    const stored = this.processesByWorkspace.get(workspaceSlug);
    return Array.isArray(stored) ? stored : [];
  }

  getPickable(workspaceSlug: string): { id: string; name: string }[] {
    return this.pickableByWorkspace.get(workspaceSlug) ?? [];
  }

  async ensurePickable(workspaceSlug: string): Promise<{ id: string; name: string }[]> {
    const cached = this.pickableByWorkspace.get(workspaceSlug);
    if (cached) return cached;
    const inflight = this.inflightPickable.get(workspaceSlug);
    if (inflight) return inflight;
    this.pickableLoading.add(workspaceSlug);
    const request = this.service
      .listPickable(workspaceSlug)
      .then((rows) => {
        runInAction(() => this.pickableByWorkspace.set(workspaceSlug, rows));
        return rows;
      })
      .catch((error: unknown) => {
        console.error("Failed to load pickable processes", { workspaceSlug, error });
        throw error;
      })
      .finally(() => {
        runInAction(() => this.pickableLoading.delete(workspaceSlug));
        this.inflightPickable.delete(workspaceSlug);
      });
    this.inflightPickable.set(workspaceSlug, request);
    return request;
  }

  /** Whether {@link ensure} has a request outstanding for this slug. Not
   *  `loading`, which is store-wide and would let another workspace's fetch
   *  drive this workspace's picker skeleton. */
  isEnsuring(workspaceSlug: string): boolean {
    return this.inflightBank.has(workspaceSlug);
  }

  /**
   * Load the full process bank unless it is already cached, sharing one
   * in-flight request between concurrent callers. Mirrors
   * `WorkspaceCustomFieldsStore.ensure`.
   *
   * Callers that only need to DISPLAY a project's process or stage read
   * `process_detail` / `current_stage_detail` off the project payload; the bank
   * is the picker's option set, so it is fetched when a picker opens rather
   * than on page load (NG-980).
   */
  async ensure(workspaceSlug: string): Promise<TProcess[]> {
    const cached = this.processesByWorkspace.get(workspaceSlug);
    if (cached) return cached;
    const inflight = this.inflightBank.get(workspaceSlug);
    if (inflight) return inflight;
    const p = this.fetch(workspaceSlug).finally(() => {
      this.inflightBank.delete(workspaceSlug);
    });
    this.inflightBank.set(workspaceSlug, p);
    return p;
  }

  getById(workspaceSlug: string, processId: string): TProcess | undefined {
    return this.get(workspaceSlug).find((p) => p.id === processId);
  }

  async fetch(workspaceSlug: string): Promise<TProcess[]> {
    this.loading = true;
    this.loadError = null;
    try {
      const rows = await this.service.list(workspaceSlug);
      runInAction(() => {
        this.processesByWorkspace.set(workspaceSlug, rows);
      });
      return rows;
    } catch (err) {
      runInAction(() => {
        this.loadError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Load the first page of the settings list with the given server query
   *  (search / type / stages / sort). Replaces the current page. */
  async loadProcessesPage(workspaceSlug: string, query: IProcessesListQuery = {}): Promise<void> {
    this.isPageLoading = true;
    this.pageError = null;
    this.lastPageQuery = query;
    try {
      const page = await this.service.listProcessesPage(workspaceSlug, { ...query, cursor: null });
      runInAction(() => {
        this.pagedProcesses = page.results;
        this.pageInfo = {
          totalCount: page.total_count,
          hasNext: page.next_page_results,
          nextCursor: page.next_cursor || null,
        };
      });
    } catch (err) {
      runInAction(() => {
        this.pageError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isPageLoading = false;
      });
    }
  }

  /** Append the next page (DataTable infinite scroll). Re-runs the first page
   *  when nothing is loaded yet (the error-empty "Try again" path). */
  async loadMoreProcesses(workspaceSlug: string): Promise<void> {
    if (this.pagedProcesses.length === 0) {
      await this.loadProcessesPage(workspaceSlug, this.lastPageQuery);
      return;
    }
    if (!this.pageInfo.hasNext || !this.pageInfo.nextCursor || this.isLoadingMore) return;
    this.isLoadingMore = true;
    this.pageError = null;
    try {
      const page = await this.service.listProcessesPage(workspaceSlug, {
        ...this.lastPageQuery,
        cursor: this.pageInfo.nextCursor,
      });
      runInAction(() => {
        const seen = new Set(this.pagedProcesses.map((p) => p.id));
        this.pagedProcesses = [
          ...this.pagedProcesses,
          ...page.results.filter((p) => !seen.has(p.id)),
        ];
        this.pageInfo = {
          totalCount: page.total_count,
          hasNext: page.next_page_results,
          nextCursor: page.next_cursor || null,
        };
      });
    } catch (err) {
      runInAction(() => {
        this.pageError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoadingMore = false;
      });
    }
  }

  /** Export the processes list as CSV, scoped to the current page query and
   *  (when `ids` is non-empty) restricted to the selected rows. */
  exportProcessesCsv(workspaceSlug: string, ids?: string[], columns?: string[]): Promise<Response> {
    const body: { ids?: string[]; columns?: string[] } = {};
    if (ids && ids.length > 0) body.ids = ids;
    if (columns && columns.length > 0) body.columns = columns;
    return this.service.exportProcesses(workspaceSlug, body, {
      q: this.lastPageQuery.q,
      type: this.lastPageQuery.type,
      stages: this.lastPageQuery.stages,
      status: this.lastPageQuery.status,
      order_by: this.lastPageQuery.order_by,
    });
  }

  pollExport(workspaceSlug: string, token: string): Promise<string | null> {
    return pollExportStatus(token, (tok) => this.service.getExportStatus(workspaceSlug, tok));
  }

  // --- Writes (NG-633) --------------------------------------------------
  // Every write re-reads the workspace bank rather than patching from its own
  // response: a stage write answers with a stage, a reorder renumbers rows it
  // was never asked about, and both caches have to agree afterwards. The bank
  // is the single source the settings page then re-syncs from.

  /** Re-read the bank after a write and re-sync the settings page row from it.
   *  A failed refresh must not read as a failed write, so it warns rather than
   *  rejecting — the write already committed. */
  private async syncAfterWrite(workspaceSlug: string, processId: string): Promise<void> {
    this.pickableByWorkspace.delete(workspaceSlug);
    let rows: TProcess[];
    try {
      rows = await this.fetch(workspaceSlug);
    } catch (error) {
      console.warn(
        `[ProcessesStore] post-write refresh failed for process ${processId} in ${workspaceSlug}`,
        error,
      );
      return;
    }
    const fresh = rows.find((p) => p.id === processId);
    runInAction(() => {
      if (!this.pagedProcesses.some((p) => p.id === processId)) return;
      if (fresh) {
        this.pagedProcesses = this.pagedProcesses.map((p) => (p.id === processId ? fresh : p));
        return;
      }
      // Gone from the bank — the process was deleted.
      this.pagedProcesses = this.pagedProcesses.filter((p) => p.id !== processId);
      this.pageInfo = { ...this.pageInfo, totalCount: Math.max(0, this.pageInfo.totalCount - 1) };
    });
  }

  /** Re-read both caches by re-running the page QUERY, rather than patching the
   *  row in place. `syncAfterWrite` only ever re-syncs a row the settings page
   *  already holds and leaves the query alone, which is right for an edit that
   *  cannot change whether the row belongs on the page — and wrong for the two
   *  writes that can. Only the server knows where a row sorts and whether the
   *  active filters still admit it. A failed refresh must not read as a failed
   *  write, so it warns rather than rejecting: the write already committed. */
  private async refreshPagedCaches(
    workspaceSlug: string,
    processId: string,
    reason: string,
  ): Promise<void> {
    this.pickableByWorkspace.delete(workspaceSlug);
    try {
      await Promise.all([
        this.fetch(workspaceSlug),
        this.loadProcessesPage(workspaceSlug, this.lastPageQuery),
      ]);
    } catch (error) {
      console.warn(
        `[ProcessesStore] post-${reason} refresh failed for process ${processId} in ${workspaceSlug}`,
        error,
      );
    }
  }

  async createProcess(
    workspaceSlug: string,
    payload: IProcessCreatePayload,
    source: AnalyticsSource = "page",
  ): Promise<TProcess> {
    const created = await this.service.create(workspaceSlug, payload);
    // Before the refresh, so a failed reload can't drop it.
    track("process_created", {
      process_id: created.id,
      stage_count: created.stages?.length ?? 0,
      source,
    });
    // A brand-new process is by definition not on the page, so `syncAfterWrite`
    // — which only re-syncs a row the page already holds — never showed it in
    // the table it was created from.
    await this.refreshPagedCaches(workspaceSlug, created.id, "create");
    return created;
  }

  async updateProcess(
    workspaceSlug: string,
    processId: string,
    payload: TProcessUpdatePayload,
    source: AnalyticsSource = "page",
  ): Promise<TProcess> {
    const before = this.processById(workspaceSlug, processId);
    const updated = await this.service.update(workspaceSlug, processId, payload);
    this.trackProcessUpdate(processId, payload, before, source);
    await this.syncAfterWrite(workspaceSlug, processId);
    return updated;
  }

  async deleteProcess(
    workspaceSlug: string,
    processId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.service.remove(workspaceSlug, processId);
    track("process_deleted", { process_id: processId, source });
    await this.syncAfterWrite(workspaceSlug, processId);
  }

  private processById(workspaceSlug: string, processId: string): TProcess | undefined {
    return this.get(workspaceSlug).find((p) => p.id === processId);
  }

  /** The Active switch sends `is_active` alone: an activation, not an edit. */
  private trackProcessUpdate(
    processId: string,
    payload: TProcessUpdatePayload,
    before: TProcess | undefined,
    source: AnalyticsSource,
  ): void {
    const keys = Object.keys(payload);
    if (keys.length === 1 && payload.is_active !== undefined) {
      track(payload.is_active ? "process_activated" : "process_deactivated", {
        process_id: processId,
        source,
      });
      return;
    }
    // The record holds its teams as objects; the payload sends their ids.
    const stored = before && { ...before, team_access: before.team_access.map((team) => team.id) };
    track("process_updated", {
      process_id: processId,
      fields: changedFields(payload, stored),
      source,
    });
  }

  async addStage(
    workspaceSlug: string,
    processId: string,
    payload: IProcessStagePayload,
    source: AnalyticsSource = "page",
  ): Promise<TProcessStage> {
    const stage = await this.service.addStage(workspaceSlug, processId, payload);
    track("process_stage_created", { process_id: processId, stage_id: stage.id, source });
    await this.syncAfterWrite(workspaceSlug, processId);
    return stage;
  }

  async updateStage(
    workspaceSlug: string,
    processId: string,
    stageId: string,
    payload: Partial<IProcessStagePayload>,
    source: AnalyticsSource = "page",
  ): Promise<TProcessStage> {
    const before = this.processById(workspaceSlug, processId)?.stages.find((s) => s.id === stageId);
    const stage = await this.service.updateStage(workspaceSlug, processId, stageId, payload);
    const ids = { process_id: processId, stage_id: stageId, source };
    // The stage's Active switch sends `is_active` alone: an activation, not an edit.
    if (Object.keys(payload).length === 1 && payload.is_active !== undefined) {
      track(payload.is_active ? "process_stage_activated" : "process_stage_deactivated", ids);
    } else {
      track("process_stage_updated", { ...ids, fields: changedFields(payload, before) });
    }
    await this.syncAfterWrite(workspaceSlug, processId);
    return stage;
  }

  /** Delete a stage, first moving its live work items to `moveTo` (NG-635).
   *  Omitting `moveTo` on a stage that holds anything is a 409 carrying the
   *  candidates — the caller turns that into the move dialog and retries. */
  async deleteStage(
    workspaceSlug: string,
    processId: string,
    stageId: string,
    moveTo?: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.service.removeStage(workspaceSlug, processId, stageId, moveTo);
    track("process_stage_deleted", {
      process_id: processId,
      stage_id: stageId,
      moved_work_items: moveTo !== undefined,
      source,
    });
    await this.syncAfterWrite(workspaceSlug, processId);
  }

  /** Retire a process, or bring it back (NG-635).
   *
   *  This is the one write whose field the settings list also FILTERS on, which
   *  is why it cannot go through `updateProcess` unconditionally: that re-syncs
   *  the row in place, so under a Status filter the row a person just retired
   *  stayed on screen wearing its new Inactive badge — on a page that says it
   *  is showing active processes — while `totalCount` and the cursors went on
   *  describing the page before the write. Re-running the page query is what
   *  drops it, and it is the same answer the create path needs.
   *
   *  Only when a Status filter is applied. Without one the row genuinely belongs
   *  where it is and only its badge changed, and reloading page 1 there would
   *  throw away every row an infinite scroll had appended to pay for nothing. */
  async setProcessActive(
    workspaceSlug: string,
    processId: string,
    isActive: boolean,
    source: AnalyticsSource = "page",
  ): Promise<TProcess> {
    if (!this.lastPageQuery.status?.length) {
      return this.updateProcess(workspaceSlug, processId, { is_active: isActive }, source);
    }
    const updated = await this.service.update(workspaceSlug, processId, { is_active: isActive });
    this.trackProcessUpdate(processId, { is_active: isActive }, undefined, source);
    await this.refreshPagedCaches(workspaceSlug, processId, "status change");
    return updated;
  }

  /** Retire a stage, or bring it back (NG-635). The work items in it do not
   *  move; the server re-derives every project's current stage in the same
   *  transaction, which is why this re-syncs the bank like any other write. */
  async setStageActive(
    workspaceSlug: string,
    processId: string,
    stageId: string,
    isActive: boolean,
    source: AnalyticsSource = "page",
  ): Promise<TProcessStage> {
    return this.updateStage(workspaceSlug, processId, stageId, { is_active: isActive }, source);
  }

  /** Persist a whole stage ordering. `stageIds` must be every active stage of
   *  the process, exactly once — the server rejects a partial list rather than
   *  guessing where the omitted stages belong. */
  async reorderStages(
    workspaceSlug: string,
    processId: string,
    stageIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<TProcess> {
    const process = await this.service.reorderStages(workspaceSlug, processId, stageIds);
    track("process_stages_reordered", { process_id: processId, count: stageIds.length, source });
    await this.syncAfterWrite(workspaceSlug, processId);
    return process;
  }

  /** Read a process's current usage (projects + templates using it, work items
   *  per stage). Deliberately uncached: the editor asks each time it opens, so
   *  a stage emptied in another tab doesn't stay un-removable. */
  loadUsage(workspaceSlug: string, processId: string): Promise<TProcessUsage> {
    return this.service.usage(workspaceSlug, processId);
  }
}

export class ProjectActivityStore {
  activitiesByProject = observable.map<string, TProjectActivity[]>();
  /** Cursor for the next (older) page per project; ``null`` when exhausted. */
  nextCursorByProject = observable.map<string, string | null>();
  loading = false;
  loadingMore = false;
  private service: ProjectActivityService;
  private rootStore: RootStore;

  constructor(rootStore: RootStore, service?: ProjectActivityService) {
    this.rootStore = rootStore;
    this.service = service ?? new ProjectActivityService();
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  get(projectId: string): TProjectActivity[] {
    const stored = this.activitiesByProject.get(projectId);
    return Array.isArray(stored) ? stored : [];
  }

  /** True when an older page is available to load via :meth:`loadMore`. */
  hasMore(projectId: string): boolean {
    return Boolean(this.nextCursorByProject.get(projectId));
  }

  /** Revalidate the feed after a mutation, but only when it has been fetched
   *  before — an unopened feed fetches fresh on first open anyway. Re-fetches
   *  the first page only (subsequent pages reload on demand). */
  async refreshIfCached(workspaceSlug: string, projectId: string): Promise<void> {
    if (!this.activitiesByProject.has(projectId)) {
      // Not shown here, but it may be cached (R3): the next read must not be
      // answered with the feed from before the write.
      const scope = queryCacheScope(this.rootStore, workspaceSlug, "ProjectActivityStore");
      if (scope) {
        await peekQueryClient()?.invalidateQueries({
          queryKey: projectActivitiesKey(scope, projectId),
          exact: true,
        });
      }
      return;
    }
    try {
      await this.fetch(workspaceSlug, projectId);
    } catch (error) {
      console.warn(
        `[ProjectActivityStore] post-mutation activity refresh failed for project ${projectId}`,
        error,
      );
    }
  }

  /**
   * The feed a page needs on arrival: what the project tab layout's loader
   * reads. With the `query-cache` flag off this is `fetch`, unchanged. With it
   * on, the read goes through the cached feed (R3,
   * `app/queries/project-resources.ts`), so re-entering the project within its
   * freshness window makes no request; the pages are then projected into
   * `activitiesByProject`, which the feed still renders from.
   */
  load(workspaceSlug: string, projectId: string): Promise<TProjectActivity[]> {
    const scope = queryCacheScope(this.rootStore, workspaceSlug, "ProjectActivityStore");
    if (!scope) return this.fetch(workspaceSlug, projectId);
    return readFeedInto(
      projectActivitiesQuery(this.service, scope, projectId),
      { force: false },
      this.feedProjection(projectId),
    );
  }

  /** Fetch the first (newest) page, replacing any accumulated pages. With the
   *  `query-cache` flag on the cached feed (R3) is refetched the same way. */
  async fetch(workspaceSlug: string, projectId: string): Promise<TProjectActivity[]> {
    const scope = queryCacheScope(this.rootStore, workspaceSlug, "ProjectActivityStore");
    if (scope) {
      return readFeedInto(
        projectActivitiesQuery(this.service, scope, projectId),
        { force: true },
        this.feedProjection(projectId),
      );
    }
    this.loading = true;
    try {
      const page = await this.service.list(workspaceSlug, projectId);
      runInAction(() => {
        this.activitiesByProject.set(projectId, page.results);
        this.nextCursorByProject.set(projectId, page.next_cursor);
      });
      return page.results;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** How a cached read of `projectId`'s feed lands in this store. */
  private feedProjection(projectId: string): FeedProjection<TProjectActivity> {
    return {
      setLoading: (loading) => (this.loading = loading),
      apply: (rows, cursor) => {
        this.activitiesByProject.set(projectId, rows);
        this.nextCursorByProject.set(projectId, cursor);
      },
    };
  }

  /** Append the next (older) page using the stored cursor. No-op when the feed
   *  is exhausted or a load is already in flight. With the `query-cache` flag
   *  on, the page is appended to the cached feed (R3) too. */
  async loadMore(workspaceSlug: string, projectId: string): Promise<void> {
    const cursor = this.nextCursorByProject.get(projectId);
    if (!cursor || this.loadingMore) return;
    this.loadingMore = true;
    try {
      const scope = queryCacheScope(this.rootStore, workspaceSlug, "ProjectActivityStore");
      if (
        scope &&
        (await readNextFeedPageInto(
          projectActivitiesQuery(this.service, scope, projectId),
          this.feedProjection(projectId).apply,
        ))
      ) {
        return;
      }
      const page = await this.service.list(workspaceSlug, projectId, { cursor });
      runInAction(() => {
        const existing = this.activitiesByProject.get(projectId) ?? [];
        this.activitiesByProject.set(projectId, [...existing, ...page.results]);
        this.nextCursorByProject.set(projectId, page.next_cursor);
      });
    } finally {
      runInAction(() => {
        this.loadingMore = false;
      });
    }
  }
}

export class ProjectCommentsStore {
  commentsByProject = observable.map<string, TProjectComment[]>();
  /** Cursor for the next (older) page per project; ``null`` when exhausted. */
  nextCursorByProject = observable.map<string, string | null>();
  loading = false;
  loadingMore = false;
  private service: ProjectCommentService;
  private rootStore: RootStore;

  constructor(rootStore: RootStore, service?: ProjectCommentService) {
    this.rootStore = rootStore;
    this.service = service ?? new ProjectCommentService();
    // exception: autoBind intentionally omitted — comments-feed.test.tsx
    // monkey-patches `create`. action.bound installs bound actions as
    // non-configurable. Tracked as a follow-up; refactor the test to inject
    // a mock ProjectCommentService through this constructor instead.
    makeAutoObservable(this, { rootStore: false } as never);
  }

  get(projectId: string): TProjectComment[] {
    const stored = this.commentsByProject.get(projectId);
    return Array.isArray(stored) ? stored : [];
  }

  /** True when an older page is available to load via :meth:`loadMore`. */
  hasMore(projectId: string): boolean {
    return Boolean(this.nextCursorByProject.get(projectId));
  }

  /**
   * The comments a page needs on arrival: what the project tab layout's loader
   * reads. With the `query-cache` flag off this is `fetch`, unchanged. With it
   * on, the read goes through the cached comments (R4,
   * `app/queries/project-resources.ts`), so re-entering the project within its
   * freshness window makes no request; the pages are then projected into
   * `commentsByProject`, which the drawer and its count still render from.
   */
  load(workspaceSlug: string, projectId: string): Promise<TProjectComment[]> {
    const scope = queryCacheScope(this.rootStore, workspaceSlug, "ProjectCommentsStore");
    if (!scope) return this.fetch(workspaceSlug, projectId);
    return readFeedInto(
      projectCommentsQuery(this.service, scope, projectId),
      { force: false },
      this.feedProjection(projectId),
    );
  }

  /** Fetch the first (newest) page, replacing any accumulated pages. With the
   *  `query-cache` flag on the cached comments (R4) are refetched the same way. */
  async fetch(workspaceSlug: string, projectId: string): Promise<TProjectComment[]> {
    const scope = queryCacheScope(this.rootStore, workspaceSlug, "ProjectCommentsStore");
    if (scope) {
      return readFeedInto(
        projectCommentsQuery(this.service, scope, projectId),
        { force: true },
        this.feedProjection(projectId),
      );
    }
    this.loading = true;
    try {
      const page = await this.service.list(workspaceSlug, projectId);
      runInAction(() => {
        this.commentsByProject.set(projectId, page.results);
        this.nextCursorByProject.set(projectId, page.next_cursor);
      });
      return page.results;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** How a cached read of `projectId`'s comments lands in this store. */
  private feedProjection(projectId: string): FeedProjection<TProjectComment> {
    return {
      setLoading: (loading) => (this.loading = loading),
      apply: (rows, cursor) => {
        this.commentsByProject.set(projectId, rows);
        this.nextCursorByProject.set(projectId, cursor);
      },
    };
  }

  /** Append the next (older) page using the stored cursor. No-op when the feed
   *  is exhausted or a load is already in flight. With the `query-cache` flag
   *  on, the page is appended to the cached comments (R4) too. */
  async loadMore(workspaceSlug: string, projectId: string): Promise<void> {
    const cursor = this.nextCursorByProject.get(projectId);
    if (!cursor || this.loadingMore) return;
    this.loadingMore = true;
    try {
      const scope = queryCacheScope(this.rootStore, workspaceSlug, "ProjectCommentsStore");
      if (
        scope &&
        (await readNextFeedPageInto(
          projectCommentsQuery(this.service, scope, projectId),
          this.feedProjection(projectId).apply,
        ))
      ) {
        return;
      }
      const page = await this.service.list(workspaceSlug, projectId, { cursor });
      runInAction(() => {
        const existing = this.commentsByProject.get(projectId) ?? [];
        this.commentsByProject.set(projectId, [...existing, ...page.results]);
        this.nextCursorByProject.set(projectId, page.next_cursor);
      });
    } finally {
      runInAction(() => {
        this.loadingMore = false;
      });
    }
  }

  async create(
    workspaceSlug: string,
    projectId: string,
    data: Partial<TProjectComment>,
    source: AnalyticsSource = "page",
  ): Promise<TProjectComment> {
    const created = await this.service.create(workspaceSlug, projectId, data);
    runInAction(() => {
      const existing = this.commentsByProject.get(projectId) ?? [];
      this.commentsByProject.set(projectId, [created, ...existing]);
    });
    this.patchCached(workspaceSlug, projectId, (results, page) =>
      page === 0 ? [created, ...results] : results,
    );
    track("project_comment_posted", { project_id: projectId, comment_id: created.id, source });
    // The backend logs a "commented" activity row; revalidate an already-open
    // activity feed so the comment shows up there without a manual refresh.
    void this.rootStore.projectActivity.refreshIfCached(workspaceSlug, projectId);
    return created;
  }

  async remove(
    workspaceSlug: string,
    projectId: string,
    commentId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.service.remove(workspaceSlug, projectId, commentId);
    runInAction(() => {
      const existing = this.commentsByProject.get(projectId) ?? [];
      this.commentsByProject.set(
        projectId,
        existing.filter((c) => c.id !== commentId),
      );
    });
    this.patchCached(workspaceSlug, projectId, (results) =>
      results.filter((c) => c.id !== commentId),
    );
    track("project_comment_deleted", { project_id: projectId, comment_id: commentId, source });
    void this.rootStore.projectActivity.refreshIfCached(workspaceSlug, projectId);
  }

  /** Keep the cached comments (R4) in step with a confirmed write, as the
   *  map above is: patched rather than refetched (contract §3.1). */
  private patchCached(
    workspaceSlug: string,
    projectId: string,
    update: (results: TProjectComment[], page: number) => TProjectComment[],
  ): void {
    const scope = queryCacheScope(this.rootStore, workspaceSlug, "ProjectCommentsStore");
    const client = peekQueryClient();
    if (scope && client) patchFeed(client, projectCommentsKey(scope, projectId), update);
  }
}

export class ProgramActivityStore {
  activitiesByProgram = observable.map<string, TProjectActivity[]>();
  /** Cursor for the next (older) page per program; ``null`` when exhausted. */
  nextCursorByProgram = observable.map<string, string | null>();
  loading = false;
  loadingMore = false;
  private service: ProgramActivityService;

  constructor(_rootStore: RootStore, service?: ProgramActivityService) {
    this.service = service ?? new ProgramActivityService();
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  get(programId: string): TProjectActivity[] {
    const stored = this.activitiesByProgram.get(programId);
    return Array.isArray(stored) ? stored : [];
  }

  hasMore(programId: string): boolean {
    return Boolean(this.nextCursorByProgram.get(programId));
  }

  /** Revalidate the feed after a mutation, but only when it has been fetched
   *  before — an unopened feed fetches fresh on first open anyway. */
  async refreshIfCached(workspaceSlug: string, programId: string): Promise<void> {
    if (!this.activitiesByProgram.has(programId)) return;
    try {
      await this.fetch(workspaceSlug, programId);
    } catch (error) {
      console.warn(
        `[ProgramActivityStore] post-mutation activity refresh failed for program ${programId}`,
        error,
      );
    }
  }

  /** Fetch the first (newest) page, replacing any accumulated pages. */
  async fetch(workspaceSlug: string, programId: string): Promise<TProjectActivity[]> {
    this.loading = true;
    try {
      const page = await this.service.list(workspaceSlug, programId);
      runInAction(() => {
        this.activitiesByProgram.set(programId, page.results);
        this.nextCursorByProgram.set(programId, page.next_cursor);
      });
      return page.results;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Append the next (older) page using the stored cursor. */
  async loadMore(workspaceSlug: string, programId: string): Promise<void> {
    const cursor = this.nextCursorByProgram.get(programId);
    if (!cursor || this.loadingMore) return;
    this.loadingMore = true;
    try {
      const page = await this.service.list(workspaceSlug, programId, { cursor });
      runInAction(() => {
        const existing = this.activitiesByProgram.get(programId) ?? [];
        this.activitiesByProgram.set(programId, [...existing, ...page.results]);
        this.nextCursorByProgram.set(programId, page.next_cursor);
      });
    } finally {
      runInAction(() => {
        this.loadingMore = false;
      });
    }
  }
}

export class ProgramCommentsStore {
  commentsByProgram = observable.map<string, TProjectComment[]>();
  /** Cursor for the next (older) page per program; ``null`` when exhausted. */
  nextCursorByProgram = observable.map<string, string | null>();
  loading = false;
  loadingMore = false;
  private service: ProgramCommentService;
  private rootStore: RootStore;

  constructor(rootStore: RootStore, service?: ProgramCommentService) {
    this.rootStore = rootStore;
    this.service = service ?? new ProgramCommentService();
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  get(programId: string): TProjectComment[] {
    const stored = this.commentsByProgram.get(programId);
    return Array.isArray(stored) ? stored : [];
  }

  hasMore(programId: string): boolean {
    return Boolean(this.nextCursorByProgram.get(programId));
  }

  /** Fetch the first (newest) page, replacing any accumulated pages. */
  async fetch(workspaceSlug: string, programId: string): Promise<TProjectComment[]> {
    this.loading = true;
    try {
      const page = await this.service.list(workspaceSlug, programId);
      runInAction(() => {
        this.commentsByProgram.set(programId, page.results);
        this.nextCursorByProgram.set(programId, page.next_cursor);
      });
      return page.results;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Append the next (older) page using the stored cursor. */
  async loadMore(workspaceSlug: string, programId: string): Promise<void> {
    const cursor = this.nextCursorByProgram.get(programId);
    if (!cursor || this.loadingMore) return;
    this.loadingMore = true;
    try {
      const page = await this.service.list(workspaceSlug, programId, { cursor });
      runInAction(() => {
        const existing = this.commentsByProgram.get(programId) ?? [];
        this.commentsByProgram.set(programId, [...existing, ...page.results]);
        this.nextCursorByProgram.set(programId, page.next_cursor);
      });
    } finally {
      runInAction(() => {
        this.loadingMore = false;
      });
    }
  }

  async create(
    workspaceSlug: string,
    programId: string,
    data: Partial<TProjectComment>,
    source: AnalyticsSource = "page",
  ): Promise<TProjectComment> {
    const created = await this.service.create(workspaceSlug, programId, data);
    runInAction(() => {
      const existing = this.commentsByProgram.get(programId) ?? [];
      this.commentsByProgram.set(programId, [created, ...existing]);
    });
    track("program_comment_posted", {
      program_id: programId,
      comment_id: created.id,
      reply: Boolean(data.parent),
      source,
    });
    // The backend logs a "commented" activity row; revalidate an already-open
    // activity feed so the comment shows up there without a manual refresh.
    void this.rootStore.programActivity.refreshIfCached(workspaceSlug, programId);
    return created;
  }
}

export class ProjectLinkedSubmissionsStore {
  linksByProject = observable.map<string, TProjectSubmissionLink[]>();
  loading = false;
  private service: ProjectLinkedSubmissionsService;
  private rootStore: RootStore;

  constructor(rootStore: RootStore, service?: ProjectLinkedSubmissionsService) {
    this.rootStore = rootStore;
    this.service = service ?? new ProjectLinkedSubmissionsService();
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  get(projectId: string): TProjectSubmissionLink[] {
    const stored = this.linksByProject.get(projectId);
    return Array.isArray(stored) ? stored : [];
  }

  /** The requests linked to a project, for the overview card. With the
   *  `query-cache` flag on, the read goes through the cached list (R6,
   *  `app/queries/project-resources.ts`), so re-entering the overview within
   *  its freshness window makes no request. */
  async fetch(workspaceSlug: string, projectId: string): Promise<TProjectSubmissionLink[]> {
    const scope = queryCacheScope(this.rootStore, workspaceSlug, "ProjectLinkedSubmissionsStore");
    this.loading = true;
    try {
      const rows = scope
        ? await getQueryClient().fetchQuery(
            projectLinkedSubmissionsQuery(this.service, scope, projectId),
          )
        : await this.service.list(workspaceSlug, projectId);
      runInAction(() => {
        this.linksByProject.set(projectId, rows);
      });
      return rows;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async link(
    workspaceSlug: string,
    projectId: string,
    submissionId: string,
    source: AnalyticsSource = "page",
  ): Promise<TProjectSubmissionLink> {
    const created = await this.service.link(workspaceSlug, projectId, submissionId);
    runInAction(() => {
      const existing = this.linksByProject.get(projectId) ?? [];
      if (!existing.find((l) => l.id === created.id)) {
        this.linksByProject.set(projectId, [...existing, created]);
      }
    });
    this.afterWrite(workspaceSlug, projectId, (links) =>
      links.some((l) => l.id === created.id) ? links : [...links, created],
    );
    // The project side of the link the request side reports as
    // request_projects_linked (NG-2057). This dialog links one at a time.
    track("project_requests_linked", {
      project_id: projectId,
      request_id: submissionId,
      count: 1,
      source,
    });
    return created;
  }

  async unlink(
    workspaceSlug: string,
    projectId: string,
    submissionId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.service.unlink(workspaceSlug, projectId, submissionId);
    runInAction(() => {
      const existing = this.linksByProject.get(projectId) ?? [];
      this.linksByProject.set(
        projectId,
        existing.filter((l) => l.id !== submissionId),
      );
    });
    this.afterWrite(workspaceSlug, projectId, (links) =>
      links.filter((l) => l.id !== submissionId),
    );
    track("project_request_unlinked", { project_id: projectId, request_id: submissionId, source });
  }

  /**
   * Keep the cache in step with a confirmed link or unlink (contract §3.1):
   * the cached list (R6) is patched like the map above; the activity feed (R3)
   * and the project detail (R1, the overview card reads a count off it) are
   * invalidated.
   */
  private afterWrite(
    workspaceSlug: string,
    projectId: string,
    update: (links: TProjectSubmissionLink[]) => TProjectSubmissionLink[],
  ): void {
    const scope = queryCacheScope(this.rootStore, workspaceSlug, "ProjectLinkedSubmissionsStore");
    const client = peekQueryClient();
    if (!scope || !client) return;
    patchScoped(client, projectLinkedSubmissionsKey(scope, projectId), update);
    for (const queryKey of [
      projectActivitiesKey(scope, projectId),
      projectDetailKey(scope, projectId),
    ]) {
      client.invalidateQueries({ queryKey, exact: true }).catch((error: unknown) => {
        console.warn("[ProjectLinkedSubmissionsStore] invalidating after a link write failed", {
          workspaceSlug,
          projectId,
          error,
        });
      });
    }
  }
}

export class ProjectV3Store {
  service: ProjectV3Service;

  constructor(_rootStore: RootStore, service?: ProjectV3Service) {
    this.service = service ?? new ProjectV3Service();
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  ackStageClearing(workspaceSlug: string, projectId: string) {
    return this.service.ackStageClearing(workspaceSlug, projectId);
  }

  duplicate(workspaceSlug: string, projectId: string) {
    return this.service.duplicate(workspaceSlug, projectId);
  }
}
